import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { CalendarEvent } from 'zitimail-shared-types';

import { addDays, dayKey, timeLabel } from './dates';

export const HOUR_PX = 44;
const SNAP = 15;
const DAY_MIN = 24 * 60;

type Drag =
  | { kind: 'select'; day: number; from: number; to: number }
  | {
      kind: 'move';
      event: CalendarEvent;
      day: number;
      start: number;
      length: number;
      grab: number;
      moved: boolean;
    }
  | { kind: 'resize'; event: CalendarEvent; day: number; start: number; end: number };

const snap = (minutes: number) => Math.max(0, Math.min(DAY_MIN, Math.round(minutes / SNAP) * SNAP));
const minutesOf = (iso: string, day: Date) => (new Date(iso).getTime() - day.getTime()) / 60_000;
const at = (day: Date, minutes: number) => new Date(day.getTime() + minutes * 60_000);

/**
 * The week and day views: a time grid you can draw on. Drag across empty
 * time to create an event; drag an event to move it (across days too); drag
 * its bottom edge to change its length. A plain click opens it.
 */
export function WeekGrid({
  days,
  eventsOn,
  colour,
  today,
  onCreate,
  onOpen,
  onMove,
}: {
  days: Date[];
  eventsOn: (key: string) => CalendarEvent[];
  colour: (event: CalendarEvent) => string;
  today: string;
  /** Where the pointer was, so a quick-create popover can open beside it. */
  onCreate: (start: Date, end: Date, point: { x: number; y: number }) => void;
  onOpen: (event: CalendarEvent) => void;
  onMove: (event: CalendarEvent, start: Date, end: Date) => void;
}): JSX.Element {
  const columns = useRef<Array<HTMLDivElement | null>>([]);
  const grid = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  // Open on the working day, not at midnight.
  useEffect(() => {
    if (grid.current) {
      grid.current.scrollTop = 7 * HOUR_PX;
    }
  }, []);

  /** The day column and minute under the pointer. */
  const locate = (e: ReactPointerEvent): { day: number; minutes: number } | null => {
    for (let i = 0; i < columns.current.length; i++) {
      const rect = columns.current[i]?.getBoundingClientRect();
      if (rect && e.clientX >= rect.left && e.clientX < rect.right) {
        return { day: i, minutes: ((e.clientY - rect.top) / HOUR_PX) * 60 };
      }
    }
    return null;
  };

  const begin = (e: ReactPointerEvent, next: Drag) => {
    if (e.button !== 0) {
      return;
    }
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    setDrag(next);
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag) {
      return;
    }
    const where = locate(e);
    if (!where) {
      return;
    }
    if (drag.kind === 'select') {
      setDrag({ ...drag, to: snap(where.minutes) });
    } else if (drag.kind === 'move') {
      const start = snap(where.minutes - drag.grab);
      if (start !== drag.start || where.day !== drag.day) {
        setDrag({ ...drag, day: where.day, start: Math.min(start, DAY_MIN - SNAP), moved: true });
      }
    } else {
      setDrag({ ...drag, end: Math.max(drag.start + SNAP, snap(where.minutes)) });
    }
  };

  const onPointerUp = (e: ReactPointerEvent) => {
    const done = drag;
    setDrag(null);
    if (!done) {
      return;
    }
    const day = days[done.day];
    if (!day) {
      return;
    }
    if (done.kind === 'select') {
      const from = Math.min(done.from, done.to);
      const to = Math.max(done.from, done.to);
      // A click rather than a drag: an hour from where it landed.
      onCreate(at(day, from), at(day, to - from >= SNAP ? to : Math.min(from + 60, DAY_MIN)), {
        x: e.clientX,
        y: e.clientY,
      });
    } else if (done.kind === 'move') {
      if (done.moved) {
        onMove(done.event, at(day, done.start), at(day, done.start + done.length));
      } else {
        onOpen(done.event);
      }
    } else {
      onMove(done.event, at(day, done.start), at(day, done.end));
    }
  };

  return (
    <div
      ref={grid}
      className={`timegrid${drag ? ' dragging' : ''}`}
      style={{ gridTemplateColumns: `52px repeat(${days.length}, minmax(0, 1fr))` }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => setDrag(null)}
    >
      <div />
      {days.map((d) => {
        const key = dayKey(d);
        return (
          <div key={key} className={`tg-head${key === today ? ' today' : ''}`}>
            {d.toLocaleDateString([], { weekday: 'short', day: 'numeric' })}
            <div className="tg-allday">
              {eventsOn(key)
                .filter((e) => e.allDay)
                .map((e) => (
                  <button
                    type="button"
                    key={`${e.href}:${e.start}`}
                    className="chip"
                    style={{ borderLeftColor: colour(e) }}
                    onClick={() => onOpen(e)}
                  >
                    {e.title}
                  </button>
                ))}
            </div>
          </div>
        );
      })}
      <div className="tg-hours">
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} style={{ height: HOUR_PX }}>
            {h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}
          </div>
        ))}
      </div>
      {days.map((d, index) => {
        const key = dayKey(d);
        const dayEnd = addDays(d, 1).getTime();
        const timed = eventsOn(key).filter(
          (e) => !e.allDay && !(drag && drag.kind !== 'select' && drag.event === e),
        );
        // Overlapping events share the column: each takes the first lane
        // free at its start.
        const lanes: number[] = [];
        const placed = timed.map((e) => {
          const from = Math.max(new Date(e.start).getTime(), d.getTime());
          const to = Math.min(new Date(e.end).getTime(), dayEnd);
          let lane = lanes.findIndex((busyUntil) => busyUntil <= from);
          if (lane < 0) {
            lane = lanes.length;
            lanes.push(to);
          } else {
            lanes[lane] = to;
          }
          return { e, from, to, lane };
        });
        const width = 100 / Math.max(1, lanes.length);
        const isNow = key === today;
        const nowMinutes = (Date.now() - d.getTime()) / 60_000;
        return (
          <div
            key={key}
            ref={(el) => {
              columns.current[index] = el;
            }}
            className="tg-day"
            style={{ height: HOUR_PX * 24 }}
            onPointerDown={(e) => {
              const where = locate(e);
              if (where) {
                const from = snap(where.minutes - SNAP / 2);
                begin(e, { kind: 'select', day: index, from, to: from });
              }
            }}
          >
            {isNow && <div className="now-line" style={{ top: (nowMinutes / 60) * HOUR_PX }} />}
            {placed.map(({ e, from, to, lane }) => (
              <div
                key={`${e.href}:${e.start}`}
                role="button"
                tabIndex={0}
                className={`tg-event${e.recurring ? ' recurring' : ''}`}
                style={{
                  top: ((from - d.getTime()) / 3_600_000) * HOUR_PX,
                  height: Math.max(18, ((to - from) / 3_600_000) * HOUR_PX - 2),
                  left: `${lane * width}%`,
                  width: `calc(${width}% - 3px)`,
                  background: colour(e),
                }}
                title={e.recurring ? `${e.title} — repeating; open it to change` : e.title}
                onKeyDown={(k) => k.key === 'Enter' && onOpen(e)}
                onPointerDown={(p) => {
                  if (e.recurring) {
                    p.stopPropagation();
                    return;
                  }
                  const where = locate(p);
                  const start = minutesOf(e.start, d);
                  begin(p, {
                    kind: 'move',
                    event: e,
                    day: index,
                    start,
                    length: (new Date(e.end).getTime() - new Date(e.start).getTime()) / 60_000,
                    grab: (where?.minutes ?? start) - start,
                    moved: false,
                  });
                }}
                onClick={() => e.recurring && onOpen(e)}
              >
                <strong>{e.title}</strong>
                <span>
                  {timeLabel(e.start)}
                  {e.location ? ` · ${e.location}` : ''}
                </span>
                {!e.recurring && (
                  <span
                    className="tg-resize"
                    aria-hidden="true"
                    onPointerDown={(p) =>
                      begin(p, {
                        kind: 'resize',
                        event: e,
                        day: index,
                        start: minutesOf(e.start, d),
                        end: minutesOf(e.end, d),
                      })
                    }
                  />
                )}
              </div>
            ))}
            {drag && drag.day === index && (
              <div
                className={`tg-ghost${drag.kind === 'select' ? ' select' : ''}`}
                style={(() => {
                  const [from, to] =
                    drag.kind === 'select'
                      ? [
                          Math.min(drag.from, drag.to),
                          Math.max(drag.from, drag.to, Math.min(drag.from, drag.to) + SNAP),
                        ]
                      : drag.kind === 'move'
                        ? [drag.start, drag.start + drag.length]
                        : [drag.start, drag.end];
                  return {
                    top: (from / 60) * HOUR_PX,
                    height: Math.max(12, ((to - from) / 60) * HOUR_PX - 2),
                    ...(drag.kind !== 'select' ? { background: colour(drag.event) } : {}),
                  };
                })()}
              >
                {drag.kind === 'select'
                  ? `${timeLabel(at(d, Math.min(drag.from, drag.to)).toISOString())} – ${timeLabel(at(d, Math.max(drag.from, drag.to, Math.min(drag.from, drag.to) + SNAP)).toISOString())}`
                  : drag.event.title}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
